import React from 'react';
import ReactDOM from 'react-dom/client';

// Top-level error boundary.
// Dev: shows full error + stack on screen so mobile debugging is possible.
// Prod: shows a neutral friendly screen, no internals leaked to end users.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error('[ErrorBoundary]', error, info);
    // React error boundaries stop the error from reaching window.onerror, so
    // PostHog's autocapture never sees it. Report it explicitly so app crashes
    // are not a blind spot. Guarded: no-ops safely if PostHog never initialized
    // (localhost / consent not given) and never blocks the fallback render.
    try {
      posthog?.captureException?.(error, {
        componentStack: info?.componentStack,
        source: 'react-error-boundary',
      });
    } catch {
      /* ignore - error reporting must never throw */
    }
  }
  render() {
    if (this.state.error) {
      if (import.meta.env.DEV) {
        return (
          <div style={{ padding: 24, fontFamily: 'system-ui', background: '#fff1f0', minHeight: '100vh' }}>
            <h2 style={{ color: '#c00', marginTop: 0 }}>App crashed - error details:</h2>
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#fff', padding: 12, borderRadius: 8, border: '1px solid #fca5a5', fontSize: 13 }}>
              {this.state.error?.message}
              {'\n\n'}
              {this.state.error?.stack}
            </pre>
          </div>
        );
      }
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#1a1a2e', color: 'white', fontFamily: 'system-ui', textAlign: 'center', padding: 20 }}>
          <div>
            <h1 style={{ margin: '0 0 20px 0' }}>Something went wrong</h1>
            <p style={{ margin: 0, opacity: 0.7 }}>Please refresh the page or try again later.</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
import { BrowserRouter } from 'react-router-dom';
import { PostHogProvider } from '@posthog/react';
import posthog from 'posthog-js';
import App from '@/App';
import '@/index.css';
import { AccessibilityProvider } from '@/contexts/AccessibilityContext';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { TtsLithuanianPreviewBridge } from '@/components/TtsLithuanianPreviewBridge.jsx';
import { RateLimitProvider } from '@/contexts/RateLimitContext';
import InAppBrowserOverlay from '@/components/InAppBrowserOverlay.jsx';
// Side effect only: captures Chrome's one-shot `beforeinstallprompt` event at
// startup so the install button works no matter when the install UI mounts.
import '@/hooks/usePWAInstall';

// Magic-link / OTP expiry: clean hash and send user to login with a friendly flow (see Login.jsx).
(function redirectSupabaseOtpExpired() {
  if (typeof window === 'undefined') return;
  const raw = window.location.hash?.replace(/^#/, '') || '';
  if (!raw) return;
  const params = new URLSearchParams(raw);
  const code = params.get('error_code') || '';
  if (code === 'otp_expired' || raw.includes('otp_expired')) {
    try {
      sessionStorage.setItem('ttt_login_link_expired', '1');
    } catch {
      /* ignore */
    }
    window.location.replace(`${window.location.origin}/login`);
  }
})();

function isPrivateOrLocalHostname(hostname) {
  if (!hostname) return true;
  const h = String(hostname).toLowerCase();
  if (h === 'localhost' || h === '127.0.0.1' || h === '[::1]') return true;
  if (/^192\.168\./.test(h)) return true;
  if (/^10\./.test(h)) return true;
  const m = h.match(/^172\.(\d+)\./);
  if (m) {
    const n = Number(m[1]);
    if (n >= 16 && n <= 31) return true;
  }
  return false;
}

// PostHog: production builds only, and never on local / private LAN hostnames.
const apiKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
const posthogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;

// Read the stored cookie decision up front so we can pick the right capture
// posture at init time (persistence + person_profiles are init-time settings).
let initialConsent = null;
try {
  if (typeof window !== 'undefined') {
    initialConsent = window.localStorage.getItem('ttt_cookie_consent');
  }
} catch {
  /* private mode / storage blocked - treat as no decision yet */
}
const hasCookieConsent = initialConsent === 'accepted';

const posthogOptions = {
  api_host: posthogHost || 'https://eu.i.posthog.com',
  autocapture: true,
  capture_pageview: true,
  capture_performance: true,
  capture_exceptions: true,
  // Anonymous-before-consent model. We DO capture aggregate product + funnel
  // analytics from the first paint so drop-off is measurable for all traffic,
  // but until the user taps Accept we do it WITHOUT tracking cookies or a
  // personal profile:
  //   - persistence 'memory'        -> no cookie / localStorage identifier
  //   - person_profiles identified_only -> anonymous events only; identify() is
  //     gated behind Accept (see CookieConsent.jsx + the FB funnel), so no email
  //     is attached to events pre-consent.
  //   - session recording stays OFF until consent.
  // On Accept we upgrade persistence to localStorage and start recording.
  // (Previously opt_out_capturing_by_default:true silently dropped everything
  // pre-consent, which is why the FB funnel had zero events in PostHog.)
  person_profiles: 'identified_only',
  persistence: hasCookieConsent ? 'localStorage' : 'memory',
  disable_session_recording: !hasCookieConsent,
  session_recording: {
    recordCanvas: false,
    maskAllInputs: false,
    maskTextSelector: '.sensitive',
    maskInputOptions: {
      password: true,
    },
  },
  property_blacklist: ['$sentry_dsn'],
};

const shouldInitializePostHog =
  import.meta.env.PROD &&
  typeof window !== 'undefined' &&
  !isPrivateOrLocalHostname(window.location.hostname) &&
  apiKey &&
  apiKey.startsWith('phc_');

if (shouldInitializePostHog) {
  posthog.init(apiKey, {
    ...posthogOptions,
    before_send: (event) => {
      if (event.properties?.['$current_url']?.includes('access_token')) {
        event.properties['$current_url'] =
          event.properties['$current_url'].split('#')[0];
      }
      if (event.properties?.['$referrer']?.includes('access_token')) {
        event.properties['$referrer'] =
          event.properties['$referrer'].split('#')[0];
      }
      return event;
    },
  });

  // A prior explicit "Decline" means capture nothing at all (overrides the
  // anonymous-by-default posture above). First-time visitors (null) stay in the
  // anonymous/memory mode until they choose.
  if (initialConsent === 'declined') {
    try {
      posthog.opt_out_capturing();
    } catch {
      /* never let consent handling break startup */
    }
  }

  if (window.location.hash.includes('access_token')) {
    window.history.replaceState(
      null,
      '',
      window.location.pathname + window.location.search
    );
  }
}

// Defensive initialization with error handling
try {
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error('Root element not found - cannot mount React app');
  }

  ReactDOM.createRoot(rootElement).render(
    <ErrorBoundary>
    {shouldInitializePostHog ? (
      <PostHogProvider client={posthog}>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <LanguageProvider>
            <AccessibilityProvider>
              <RateLimitProvider>
                <TtsLithuanianPreviewBridge />
                <App />
                <InAppBrowserOverlay />
              </RateLimitProvider>
            </AccessibilityProvider>
          </LanguageProvider>
        </BrowserRouter>
      </PostHogProvider>
    ) : (
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <LanguageProvider>
          <AccessibilityProvider>
            <RateLimitProvider>
              <TtsLithuanianPreviewBridge />
              <App />
              <InAppBrowserOverlay />
            </RateLimitProvider>
          </AccessibilityProvider>
        </LanguageProvider>
      </BrowserRouter>
    )}
    </ErrorBoundary>
  );

} catch (error) {
  console.error('❌ Failed to initialize React app:', error);
  // Display fallback message
  document.body.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100vh; background: #1a1a2e; color: white; font-family: system-ui; text-align: center; padding: 20px;"><div><h1 style="margin: 0 0 20px 0;">Loading Time-to-Talk</h1><p style="margin: 0; opacity: 0.7;">If this persists, try refreshing or clearing your browser cache.</p></div></div>';
}